import { z } from 'zod';

/**
 * Cursor pagination query (D-011), with the page controls added by D-1258: `?cursor=&page=&limit=`.
 *
 * `cursor` and `page` are two ways to ask for the same thing and a caller sends ONE of them. The
 * cursor is the keyset one this product has always had — stable under inserts, which is what a
 * feed that grows while you read it needs. `page` is the one a table of records wants, because a
 * table has numbered pages and a Previous button and neither can be expressed as "the row after
 * this one".
 *
 * A request carrying both is answered by the cursor: it is the more specific instruction, and
 * silently ignoring a cursor would skip rows the caller has not seen.
 */
export const paginationQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  /** 1-based. Absent means the first page, which is also what `page=1` means. */
  page: z.coerce.number().int().min(1).max(100_000).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/**
 * Where the caller is in the list, and what they can ask for next (D-1258).
 *
 * Every list answer carries this, including the ones that return everything they have in one go —
 * a client that draws pagination controls should not have to know which kind of list it is
 * looking at, and a list of 12 rows saying "page 1 of 1, nothing either side" is the honest
 * version of that.
 *
 * `total` and `pages` are NULLABLE and that is deliberate rather than lazy. Counting the rows
 * matching a filter is a second query over the same predicate, and on the big tables — messages,
 * audit events, push deliveries — it is the expensive half of the request. An endpoint that can
 * afford the count sends it; one that cannot sends null, and a client renders "Next" instead of
 * "Page 3 of 97". A number that was never computed must not be faked as 0: that reads as an empty
 * list, which is the one thing it is not.
 */
export const pageInfoSchema = z.object({
  /**
   * 1-based ordinal of this page, or NULL when the list is being walked by cursor.
   *
   * Nullable because a keyset walk genuinely has no page number: "the 30 rows after this one" is
   * not the third of anything, and the walk may have skipped or repeated rows as the table
   * changed underneath it. Answering `3` there would be a number a client could put in a control
   * and then fail to navigate back to.
   */
  number: z.number().int().min(1).nullable(),
  /** How many rows were asked for — the `limit` that produced this answer. */
  size: z.number().int().min(1),
  /** Rows matching the filters across every page, or null where counting them is refused. */
  total: z.number().int().nonnegative().nullable(),
  /** Pages at this `size`, or null wherever `total` is. */
  pages: z.number().int().nonnegative().nullable(),
  hasPrev: z.boolean(),
  hasNext: z.boolean(),
  /**
   * The cursor that walks BACK one page, for a keyset list being read with cursors. Null on a
   * page-numbered list, where the previous page is `number - 1` and needs nothing carried.
   */
  prevCursor: z.string().nullable(),
});
export type PageInfo = z.infer<typeof pageInfoSchema>;

/**
 * Wraps an item schema into the `{ items, nextCursor, page }` list envelope.
 *
 * `nextCursor` stays exactly as it was: it is what every existing client reads, and the keyset
 * walk it describes is still the right way to read a feed. `page` is beside it, not instead of
 * it, so one answer serves both a "load more" list and a table with numbered pages.
 */
export function paginated<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    page: pageInfoSchema.optional(),
  });
}

/**
 * Reads the page block off a list answer, filling in what a server too old to send one implies.
 *
 * ── WHY `page` IS OPTIONAL IN THE SCHEMA WHEN THE SERVER ALWAYS SENDS IT ────
 *
 * It is not optional on the wire. `plugins/pagination.ts` fills it in for every list answer
 * before serialization, so a response without one does not exist from a current server.
 *
 * It is optional in the SCHEMA because `fastify-type-provider-zod` types a handler's return value
 * as the response schema's output, so a required `page` obliges all 166 list handlers in the API
 * to restate a block the server derives for them — and restating it is how forty of them end up
 * subtly disagreeing. Optional is what lets the derivation live in one place and still typecheck.
 *
 * The cost is this function: clients read the block through it rather than touching `.page`, so
 * the one case that cannot happen is handled once instead of being `?.` everywhere.
 */
export function pageInfoOf(answer: {
  items: unknown[];
  nextCursor: string | null;
  page?: PageInfo;
}): PageInfo {
  return (
    answer.page ?? {
      number: null,
      size: answer.items.length || 1,
      total: answer.nextCursor === null ? answer.items.length : null,
      pages: answer.nextCursor === null ? 1 : null,
      hasPrev: false,
      hasNext: answer.nextCursor !== null,
      prevCursor: null,
    }
  );
}
